import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import './FisheryCharts.css';

// Helper function to format large numbers (K for thousands, M for millions)
const formatLargeNumber = (value) => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  } else {
    return value.toFixed(0);
  }
};

/**
 * Renders a time series chart for fishery data
 * @param {Object} props - Component props
 * @param {Array} props.data - Time series data for the chart
 * @param {string} props.title - Chart title
 * @param {function} props.valueFormatter - Function to format values for display
 * @param {string} props.yAxisLabel - Label for the Y axis
 * @param {number} props.maxHeight - Maximum height for the chart (default: 260)
 */
export const FisheryTimeSeriesChart = ({ 
  data, 
  title, 
  valueFormatter = (d) => d.toLocaleString(), 
  yAxisLabel,
  maxHeight = 250
}) => {
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);
  
  useEffect(() => {
    if (!data || !data.length || !chartRef.current) return;
    
    // Clear any existing chart and tooltip
    if (tooltipRef.current) {
      tooltipRef.current.remove();
    }
    
    // Create tooltip and append to body instead of chart container
    tooltipRef.current = d3.select('body')
      .append("div")
      .attr("class", "chart-tooltip")
      .style("opacity", 0)
      .style("position", "absolute")
      .style("z-index", "9999");
    
    // Clear any existing chart
    const svg = d3.select(chartRef.current);
    svg.selectAll("*").remove();

    // Set up dimensions and margins
    const margin = { top: 20, right: 30, bottom: 50, left: 40 };
    const svgNode = chartRef.current;
    const width = svgNode.clientWidth - margin.left - margin.right;
    const height = maxHeight - margin.top - margin.bottom;

    // Find min and max values for the scale
    const allValues = data.flatMap(d => [
      d.actual + (d.actualUncertainty || 0),
      d.actual - (d.actualUncertainty || 0),
      d.potential + (d.potentialUncertainty || 0),
      d.potential - (d.potentialUncertainty || 0)
    ]);
    
    const yMin = Math.max(0, Math.min(...allValues));
    const yMax = Math.max(...allValues) * 1.1; // Add 10% padding

    // Create scales
    const xScale = d3.scaleBand()
      .domain(data.map(d => d.year))
      .range([0, width])
      .padding(0.1);

    const yScale = d3.scaleLinear()
      .domain([yMin, yMax])
      .range([height, 0]);

    // Create chart group
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Add historical background shading (years <= 2024)
    const historicalYears = data.filter(d => parseInt(d.year) <= 2024);
    if (historicalYears.length > 0) {
      const firstHistoricalYear = Math.min(...historicalYears.map(d => parseInt(d.year)));
      const lastHistoricalYear = 2024;
      
      // Find the x positions for the shading
      const firstYearX = xScale(firstHistoricalYear) || 0;
      const lastYearData = historicalYears.find(d => parseInt(d.year) === lastHistoricalYear);
      const lastYearX = lastYearData ? 
        xScale(lastHistoricalYear) + xScale.bandwidth() : 
        xScale(Math.max(...historicalYears.map(d => parseInt(d.year)))) + xScale.bandwidth();
      
      g.append("rect")
        .attr("class", "historical-background")
        .attr("x", firstYearX)
        .attr("y", 0)
        .attr("width", lastYearX - firstYearX)
        .attr("height", height)
        .style("fill", "rgba(200, 200, 200, 0.1)")
        .style("stroke", "none");
    }

    // Add grid lines
    g.selectAll("grid-line")
      .data(yScale.ticks(5))
      .enter()
      .append("line")
      .attr("class", "grid-line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", d => yScale(d))
      .attr("y2", d => yScale(d));

    // Add x-axis
    g.append("g")
      .attr("class", "chart-axis")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale)
        .tickValues(xScale.domain().filter((d, i) => {
          // Show every 2 years if there are many data points
          return data.length > 10 ? i % 2 === 0 : true;
        })))
      .selectAll("text")
      .style("text-anchor", "middle");

    // Add y-axis with formatted values
    g.append("g")
      .attr("class", "chart-axis")
      .call(d3.axisLeft(yScale)
        .ticks(5)
        .tickFormat(d => formatLargeNumber(d)));

    // Add x-axis label
    g.append("text")
      .attr("class", "axis-label")
      .attr("x", width / 2)
      .attr("y", height + margin.bottom - 5)
      .style("text-anchor", "middle")
      .text("Year");

    // Add y-axis label
    g.append("text")
      .attr("class", "axis-label")
      .attr("x", -50)
      .attr("y", -10)
      .style("text-anchor", "start")
      .text(yAxisLabel || title);

    // Create line generators
    const actualLine = d3.line()
      .x(d => xScale(d.year) + xScale.bandwidth() / 2)
      .y(d => yScale(d.actual))
      .curve(d3.curveMonotoneX);

    const potentialLine = d3.line()
      .x(d => xScale(d.year) + xScale.bandwidth() / 2)
      .y(d => yScale(d.potential))
      .curve(d3.curveMonotoneX);

    const missedArea = d3.area()
      .x(d => xScale(d.year) + xScale.bandwidth() / 2)
      .y0(d => yScale(d.actual))
      .y1(d => yScale(d.potential))
      .curve(d3.curveMonotoneX);

    // Add missed opportunity area
    g.append("path")
      .datum(data)
      .attr("class", "chart-area")
      .attr("d", missedArea);

    // Add the potential line
    g.append("path")
      .datum(data)
      .attr("class", "potential-line")
      .attr("d", potentialLine);

    // Add the actual line
    g.append("path")
      .datum(data)
      .attr("class", "actual-line")
      .attr("d", actualLine);

    // Add uncertainty bands for actual and potential values (2024 and later only)
    const forecastData = data.filter(d => parseInt(d.year) >= 2024);
    
    if (forecastData.length > 0) {
      // Create uncertainty bands using area generators
      const actualUncertaintyArea = d3.area()
        .x(d => xScale(d.year) + xScale.bandwidth() / 2)
        .y0(d => yScale(d.actual - (d.actualUncertainty || 0)))
        .y1(d => yScale(d.actual + (d.actualUncertainty || 0)))
        .curve(d3.curveMonotoneX);

      const potentialUncertaintyArea = d3.area()
        .x(d => xScale(d.year) + xScale.bandwidth() / 2)
        .y0(d => yScale(d.potential - (d.potentialUncertainty || 0)))
        .y1(d => yScale(d.potential + (d.potentialUncertainty || 0)))
        .curve(d3.curveMonotoneX);

      // Add actual uncertainty band
      g.append("path")
        .datum(forecastData)
        .attr("class", "uncertainty-band actual-uncertainty")
        .attr("d", actualUncertaintyArea)
        .style("fill", "#4CAF50")
        .style("opacity", 0.4)
        .style("pointer-events", "none");

      // Add potential uncertainty band
      g.append("path")
        .datum(forecastData)
        .attr("class", "uncertainty-band potential-uncertainty")
        .attr("d", potentialUncertaintyArea)
        .style("fill", "#2196F3")
        .style("opacity", 0.4)
        .style("pointer-events", "none");
    }

    // Add data points
    g.selectAll(".actual-point")
      .data(data)
      .enter()
      .append("circle")
      .attr("class", "data-point actual-point")
      .attr("cx", d => xScale(d.year) + xScale.bandwidth() / 2)
      .attr("cy", d => yScale(d.actual))
      .attr("r", 3);

    g.selectAll(".potential-point")
      .data(data)
      .enter()
      .append("circle")
      .attr("class", "data-point potential-point")
      .attr("cx", d => xScale(d.year) + xScale.bandwidth() / 2)
      .attr("cy", d => yScale(d.potential))
      .attr("r", 3);

    // Add mouse events for actual points
    g.selectAll(".actual-point")
      .on("mouseover", function(event, d) {
        d3.select(this).attr("r", 5);
        
        // Highlight the year column
        const yearX = xScale(d.year) + xScale.bandwidth() / 2;
        g.append("line")
          .attr("class", "year-highlight")
          .attr("x1", yearX)
          .attr("x2", yearX)
          .attr("y1", 0)
          .attr("y2", height)
          .attr("stroke", "#666")
          .attr("stroke-width", 2)
          .attr("stroke-dasharray", "3,3")
          .attr("opacity", 0.7);
        
        // Calculate missed uncertainty
        const missedUncertainty = Math.sqrt(
          (d.potentialUncertainty || 0) ** 2 + (d.actualUncertainty || 0) ** 2
        );
        // Calculate tooltip position
        const tooltip = tooltipRef.current;
        const tooltipNode = tooltip.node();
        const rect = tooltipNode.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        
        // Position tooltip
        let left = event.pageX + 10;
        let top = event.pageY - 28;
        
        // Prevent tooltip from going off-screen
        if (left + rect.width > windowWidth) {
          left = event.pageX - rect.width - 10;
        }
        if (top + rect.height > windowHeight) {
          top = event.pageY - rect.height - 10;
        }
        if (top < 0) {
          top = event.pageY + 10;
        }
        
        tooltip
          .style("opacity", 1)
          .html(`
            <p class="tooltip-title">${d.year}</p>
            <p><strong>Catch Limit:</strong> <span style="color:rgb(33, 150, 243)">${valueFormatter(d.potential)} ± ${valueFormatter(d.potentialUncertainty || 0)}</span></p>
            <p><strong>Actual:</strong> <span style="color:rgb(27, 105, 45)">${valueFormatter(d.actual)} ± ${valueFormatter(d.actualUncertainty || 0)}</span></p>
            <p><strong>Missed:</strong> <span style="color: rgb(146, 39, 39)">${valueFormatter(d.potential - d.actual)} ± ${valueFormatter(missedUncertainty)}</span></p>
          `)
          .style("left", `${left}px`)
          .style("top", `${top}px`);
      })
      .on("mouseout", function() {
        d3.select(this).attr("r", 3);
        // Remove the year highlight
        g.selectAll(".year-highlight").remove();
        tooltipRef.current.style("opacity", 0);
      });

    // Add mouse events for potential points
    g.selectAll(".potential-point")
      .on("mouseover", function(event, d) {
        d3.select(this).attr("r", 5);
        
        // Highlight the year column
        const yearX = xScale(d.year) + xScale.bandwidth() / 2;
        g.append("line")
          .attr("class", "year-highlight")
          .attr("x1", yearX)
          .attr("x2", yearX)
          .attr("y1", 0)
          .attr("y2", height)
          .attr("stroke", "#666")
          .attr("stroke-width", 2)
          .attr("stroke-dasharray", "3,3")
          .attr("opacity", 0.7);
        
        // Calculate missed uncertainty
        const missedUncertainty = Math.sqrt(
          (d.potentialUncertainty || 0) ** 2 + (d.actualUncertainty || 0) ** 2
        );
        // Calculate tooltip position
        const tooltip = tooltipRef.current;
        const tooltipNode = tooltip.node();
        const rect = tooltipNode.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        
        // Position tooltip
        let left = event.pageX + 10;
        let top = event.pageY - 28;
        
        // Prevent tooltip from going off-screen
        if (left + rect.width > windowWidth) {
          left = event.pageX - rect.width - 10;
        }
        if (top + rect.height > windowHeight) {
          top = event.pageY - rect.height - 10;
        }
        if (top < 0) {
          top = event.pageY + 10;
        }
        
        tooltip
          .style("opacity", 1)
          .html(`
            <p class="tooltip-title">${d.year}</p>
            <p><strong>Catch Limit:</strong> <span style="color:rgb(39, 73, 209)">${valueFormatter(d.potential)} ± ${valueFormatter(d.potentialUncertainty || 0)}</span></p>
            <p><strong>Actual:</strong> <span style="color:rgb(27, 105, 45)">${valueFormatter(d.actual)} ± ${valueFormatter(d.actualUncertainty || 0)}</span></p>
            <p><strong>Missed:</strong> <span style="color: rgb(146, 39, 39)">${valueFormatter(d.potential - d.actual)} ± ${valueFormatter(missedUncertainty)}</span></p>
          `)
          .style("left", `${left}px`)
          .style("top", `${top}px`);
      })
      .on("mouseout", function() {
        d3.select(this).attr("r", 3);
        // Remove the year highlight
        g.selectAll(".year-highlight").remove();
        tooltipRef.current.style("opacity", 0);
      });
      
    // Clean up function to remove tooltip when component unmounts
    return () => {
      if (tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }
    };
  }, [data, title, valueFormatter, yAxisLabel, maxHeight]);

  return (
    <div className="fishery-chart-container">
      <div className="chart-legend">
        <div className="legend-item">
          <div className="legend-color actual"></div>
          <div className="legend-label">Actual Landings</div>
        </div>
        <div className="legend-item">
          <div className="legend-color potential"></div>
          <div className="legend-label">Annual Catch Limit</div>
        </div>
        <div className="legend-item">
          <div className="legend-color missed"></div>
          <div className="legend-label">Missed Opportunity</div>
        </div>
      </div>
      <div className="plot-content">
        <svg ref={chartRef} className="chart-svg"></svg>
      </div>
    </div>
  );
};

/**
 * Enhanced chart for OFL, ABC, ACL, Sub ACL, and Landings with interactive checkboxes
 * @param {Object} props - Component props
 * @param {Array} props.data - Time series data with ofl, abc, acl, subAcl, landings fields
 */
export const BiomassOflAbcChart = ({ data, maxHeight = 250 }) => {
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);
  
  // State for managing which metrics are visible
  const [visibleMetrics, setVisibleMetrics] = useState({
    ofl: true,
    abc: true,
    acl: true,
    landings: true
  });
  
  const formatWeight = (value) => {
    return formatLargeNumber(value);
  };
  
  // Define colors and configuration for each metric
  const metricsConfig = useMemo(() => ({
    ofl: { 
      color: "#dc2626", 
      label: "OFL", 
      name: "OFL",
      uncertaintyField: "oflUncertainty"
    },
    abc: { 
      color: "#16a34a", 
      label: "ABC", 
      name: "ABC",
      uncertaintyField: "abcUncertainty"
    },
    acl: { 
      color: "#2563eb", 
      label: "ACL", 
      name: "ACL",
      uncertaintyField: "aclUncertainty"
    },
    landings: { 
      color: "#ea580c", 
      label: "Landings", 
      name: "Landings",
      uncertaintyField: "landingsUncertainty"
    }
  }), []);
  
  // Calculate and memoize the fixed Y-axis scale based on ALL data at component mount
  const fixedYScale = useMemo(() => {
    if (!data || !data.length) return { yMin: 0, yMax: 100 };
    
    // Find min and max values across ALL metrics (not just visible ones)
    const allValues = data.flatMap(d => 
      Object.keys(metricsConfig).flatMap(metric => {
        const value = d[metric] || 0;
        const uncertainty = d[metricsConfig[metric].uncertaintyField] || 0;
        return [value + uncertainty, value - uncertainty];
      })
    );
    
    const yMin = Math.max(0, Math.min(...allValues));
    const yMax = Math.max(...allValues) * 1.1; // Add 10% padding
    
    return { yMin, yMax };
  }, [data, metricsConfig]); // Include metricsConfig in dependencies
  
  // Handle checkbox changes
  const handleMetricToggle = (metric) => {
    setVisibleMetrics(prev => ({
      ...prev,
      [metric]: !prev[metric]
    }));
  };
  
  // Get currently visible metrics
  const getVisibleMetrics = useCallback(() => {
    return Object.keys(metricsConfig).filter(metric => visibleMetrics[metric]);
  }, [metricsConfig, visibleMetrics]);
  
  useEffect(() => {
    if (!data || !data.length || !chartRef.current) return;
    
    // Clear any existing chart and tooltip
    if (tooltipRef.current) {
      tooltipRef.current.remove();
    }
    
    // Create tooltip and append to body
    tooltipRef.current = d3.select('body')
      .append("div")
      .attr("class", "chart-tooltip")
      .style("opacity", 0)
      .style("position", "absolute")
      .style("z-index", "9999");
    
    // Clear any existing chart
    const svg = d3.select(chartRef.current);
    svg.selectAll("*").remove();

    // Set up dimensions and margins
    const margin = { top: 20, right: 30, bottom: 50, left: 40 };
    const svgNode = chartRef.current;
    const width = svgNode.clientWidth - margin.left - margin.right;
    const height = maxHeight - margin.top - margin.bottom;

    // Get visible metrics
    const activeMetrics = getVisibleMetrics();
    
    if (activeMetrics.length === 0) {
      // If no metrics are visible, show a message
      const g = svg.append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);
      
      g.append("text")
        .attr("x", width / 2)
        .attr("y", height / 2)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("fill", "#666")
        .text("Select metrics to display using checkboxes above");
      
      return;
    }

    // Use the fixed Y-axis scale calculated at component mount
    const yMin = fixedYScale.yMin;
    const yMax = fixedYScale.yMax;

    // Create scales
    const xScale = d3.scaleBand()
      .domain(data.map(d => d.year))
      .range([0, width])
      .padding(0.1);

    const yScale = d3.scaleLinear()
      .domain([yMin, yMax])
      .range([height, 0]);

    // Create chart group
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Add historical background shading (years <= 2024)
    const historicalYears = data.filter(d => parseInt(d.year) <= 2024);
    if (historicalYears.length > 0) {
      const firstHistoricalYear = Math.min(...historicalYears.map(d => parseInt(d.year)));
      const lastHistoricalYear = 2024;
      
      // Find the x positions for the shading
      const firstYearX = xScale(firstHistoricalYear) || 0;
      const lastYearData = historicalYears.find(d => parseInt(d.year) === lastHistoricalYear);
      const lastYearX = lastYearData ? 
        xScale(lastHistoricalYear) + xScale.bandwidth() : 
        xScale(Math.max(...historicalYears.map(d => parseInt(d.year)))) + xScale.bandwidth();
      
      g.append("rect")
        .attr("class", "historical-background")
        .attr("x", firstYearX)
        .attr("y", 0)
        .attr("width", lastYearX - firstYearX)
        .attr("height", height)
        .style("fill", "rgba(200, 200, 200, 0.1)")
        .style("stroke", "none");
    }

    // Add grid lines
    g.selectAll("grid-line")
      .data(yScale.ticks(5))
      .enter()
      .append("line")
      .attr("class", "grid-line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", d => yScale(d))
      .attr("y2", d => yScale(d))
      .style("stroke", "#e5e7eb")
      .style("stroke-width", 1)
      .style("stroke-dasharray", "3,3");

    // Add x-axis
    g.append("g")
      .attr("class", "chart-axis")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale)
        .tickValues(xScale.domain().filter((d, i) => {
          // Show every 2 years if there are many data points
          return data.length > 10 ? i % 2 === 0 : true;
        })))
      .selectAll("text")
      .style("text-anchor", "middle");

    // Add y-axis with formatted values
    g.append("g")
      .attr("class", "chart-axis")
      .call(d3.axisLeft(yScale)
        .ticks(5)
        .tickFormat(d => formatLargeNumber(d)));

    // Add x-axis label
    g.append("text")
      .attr("class", "axis-label")
      .attr("x", width / 2)
      .attr("y", height + margin.bottom - 5)
      .style("text-anchor", "middle")
      .style("font-size", "12px")
      .style("fill", "#374151")
      .text("Year");

    // Add y-axis label
    g.append("text")
      .attr("class", "axis-label")
      .attr("x", -40)
      .attr("y", -10)
      .style("text-anchor", "start")
      .style("font-size", "12px")
      .style("fill", "#374151")
      .text("Weight (MT)");

    // Render lines and points for each visible metric
    activeMetrics.forEach(metric => {
      const config = metricsConfig[metric];
      
      // Create line generator
      const line = d3.line()
        .x(d => xScale(d.year) + xScale.bandwidth() / 2)
        .y(d => yScale(d[metric] || 0))
        .curve(d3.curveMonotoneX);

      // Add line for this metric
      g.append("path")
        .datum(data)
        .attr("class", `${metric}-line`)
        .attr("d", line)
        .style("stroke", config.color)
        .style("stroke-width", 2)
        .style("fill", "none");

      // Add data points
      g.selectAll(`.${metric}-point`)
        .data(data)
        .enter()
        .append("circle")
        .attr("class", `data-point ${metric}-point`)
        .attr("cx", d => xScale(d.year) + xScale.bandwidth() / 2)
        .attr("cy", d => yScale(d[metric] || 0))
        .attr("r", 3)
        .style("fill", config.color)
        .style("stroke", "white")
        .style("stroke-width", 1);

      // Add uncertainty band for this metric (2024 and later)
      const forecastData = data.filter(d => parseInt(d.year) >= 2024);
      const metricForecastData = forecastData.filter(d => d[config.uncertaintyField]);
      
      if (metricForecastData.length > 0) {
        const uncertaintyArea = d3.area()
          .x(d => xScale(d.year) + xScale.bandwidth() / 2)
          .y0(d => yScale((d[metric] || 0) - (d[config.uncertaintyField] || 0)))
          .y1(d => yScale((d[metric] || 0) + (d[config.uncertaintyField] || 0)))
          .curve(d3.curveMonotoneX);

        g.append("path")
          .datum(metricForecastData)
          .attr("class", `uncertainty-band ${metric}-uncertainty`)
          .attr("d", uncertaintyArea)
          .style("fill", config.color)
          .style("opacity", 0.4)
          .style("pointer-events", "none");
      }
    });

    // Add mouse events for data points
    g.selectAll(".data-point")
      .on("mouseover", function(event, d) {
        d3.select(this).attr("r", 5);
        
        // Highlight the year column
        const yearX = xScale(d.year) + xScale.bandwidth() / 2;
        g.append("line")
          .attr("class", "year-highlight")
          .attr("x1", yearX)
          .attr("x2", yearX)
          .attr("y1", 0)
          .attr("y2", height)
          .attr("stroke", "#666")
          .attr("stroke-width", 2)
          .attr("stroke-dasharray", "3,3")
          .attr("opacity", 0.7);
        
        // Build tooltip content for ALL metrics (not just visible ones)
        const tooltipContent = [`<p class="tooltip-title">${d.year}</p>`];
        
        Object.keys(metricsConfig).forEach(metric => {
          const config = metricsConfig[metric];
          const value = d[metric] || 0;
          const uncertainty = d[config.uncertaintyField];
          const uncertaintyText = uncertainty ? ` ± ${formatWeight(uncertainty)}` : '';
          
          // Show all metrics in tooltip, but highlight visible ones
          const isVisible = visibleMetrics[metric];
          const opacity = isVisible ? 1.0 : 0.6;
          const fontWeight = isVisible ? 'bold' : 'normal';
          
          tooltipContent.push(
            `<p style="opacity: ${opacity}"><span style="font-weight: ${fontWeight}">${config.name}:</span> <span style="color:${config.color}">${formatWeight(value)}${uncertaintyText}</span></p>`
          );
        });
        
        // Calculate tooltip position
        const tooltip = tooltipRef.current;
        const tooltipNode = tooltip.node();
        const rect = tooltipNode.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        
        // Position tooltip
        let left = event.pageX + 10;
        let top = event.pageY - 28;
        
        // Prevent tooltip from going off-screen
        if (left + rect.width > windowWidth) {
          left = event.pageX - rect.width - 10;
        }
        if (top + rect.height > windowHeight) {
          top = event.pageY - rect.height - 10;
        }
        if (top < 0) {
          top = event.pageY + 10;
        }
        
        tooltip
          .style("opacity", 1)
          .html(tooltipContent.join(''))
          .style("left", `${left}px`)
          .style("top", `${top}px`);
      })
      .on("mouseout", function() {
        d3.select(this).attr("r", 3);
        // Remove the year highlight
        g.selectAll(".year-highlight").remove();
        tooltipRef.current.style("opacity", 0);
      });
      
    // Clean up function to remove tooltip when component unmounts
    return () => {
      if (tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }
    };
  }, [data, visibleMetrics, fixedYScale, getVisibleMetrics, metricsConfig, maxHeight]);

  return (
    <div className="fishery-chart-container">
      <div className="chart-legend">
        {Object.entries(metricsConfig).map(([metric, config]) => (
          <div key={metric} className="legend-item">
            <label className="legend-checkbox-label">
              <input
                type="checkbox"
                checked={visibleMetrics[metric]}
                onChange={() => handleMetricToggle(metric)}
                className="legend-checkbox"
              />
              <span className="legend-checkbox-custom">
                <span className="legend-checkbox-checkmark">✓</span>
              </span>
              <div 
                className="legend-color" 
                style={{
                  backgroundColor: config.color,
                  opacity: visibleMetrics[metric] ? 1 : 0.3
                }}
              ></div>
              <span 
                className="legend-label"
                style={{
                  color: visibleMetrics[metric] ? '#374151' : '#9ca3af'
                }}
              >
                {config.label}
              </span>
            </label>
          </div>
        ))}
      </div>
      <div className="plot-content">
        <svg ref={chartRef} className="chart-svg"></svg>
      </div>
    </div>
  );
};

/**
 * Wrapper for Economic Impact Chart
 */
export const EconomicImpactChart = ({ data, currency = '$', maxHeight }) => {
  const formatCurrency = (value) => {
    return `${currency}${formatLargeNumber(value)}`;
  };
  
  return (
    <FisheryTimeSeriesChart
      data={data}
      title="ECONOMIC IMPACT"
      yAxisLabel="Economic Impact ($)"
      valueFormatter={formatCurrency}
      maxHeight={maxHeight}
    />
  );
};

/**
 * Wrapper for Meals Produced Chart
 */
export const MealsProducedChart = ({ data, maxHeight }) => {
  const formatMeals = (value) => {
    return formatLargeNumber(value);
  };
  
  return (
    <FisheryTimeSeriesChart
      data={data}
      title="MEALS PRODUCED"
      yAxisLabel="Meals Produced"
      valueFormatter={formatMeals}
      maxHeight={maxHeight}
    />
  );
};

/**
 * Wrapper for Ex Vessel Value Chart
 */
export const ExVesselValueChart = ({ data, currency = '$', maxHeight }) => {
  const formatCurrency = (value) => {
    return `${currency}${formatLargeNumber(value)}`;
  };
  
  return (
    <FisheryTimeSeriesChart
      data={data}
      title="EX VESSEL VALUE"
      yAxisLabel="Ex Vessel Value"
      valueFormatter={formatCurrency}
      maxHeight={maxHeight}
    />
  );
};

const FisheryCharts = {
  BiomassOflAbcChart,
  EconomicImpactChart,
  MealsProducedChart,
  ExVesselValueChart
};

export default FisheryCharts;